import React, { useEffect } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import {
  DataGrid,
  baseballLogicService,
  DefaultContextMenuOptionsBuilder,
  IDataGridColumnConfiguration,
  IBulkPlayerEditorMetadata,
  IPlayer,
} from '@trackman/baseball-shared-components';
import { observer } from 'mobx-react-lite';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ReviewGame } from 'models/review/ReviewGame';
import { playerToReviewPlayerMap } from 'services/helper-services/mappingHelpers/playerMappings';
import { ColumnName } from 'enums/ColumnName';
import { StatusCell } from 'pages/Review/Components/CellComponents/StatusCell/StatusCell';
import { emptyApiPlayLineup, emptyCustomMetadata } from 'services/helper-services/constants';
import { GameReviewStore } from '../../../stores/GameReviewStore';
import { ReviewDataRow } from '../../../models/review/ReviewDataRow/ReviewDataRow';
import { IApiPlayWithBallFinals } from '../../../interfaces/session/IApiPlayWithBallFinals';
import { DataKey } from '../../../enums/DataKey';
import { SessionReviewRowManager } from '../../../models/review/SessionReviewRowManager';
import { BpColumnConfigurations } from '../../Review/Components/SessionReviewColumnConfigurations';
import { noteResolvedValidator } from '../../../services/helper-services/reviewValidators/NoteResolvedValidator';
import { BpPlaysGridWrapper } from './BpPlaysGridStyles';

interface IProps {
  session: ReviewGame;
  rowManager: SessionReviewRowManager;
}
export const BpPlaysGrid: React.FC<IProps> = observer((props): React.ReactElement => {
  const { session, rowManager } = props;

  const STICKY_COLUMN_WIDTH = 173;

  const reviewStore = useInjection<GameReviewStore>(ServiceTypes.GameReviewStore);

  const createEmptyRow = () => {
    const apiPlay = {
      play: {
        ...baseballLogicService.createEmptyApiPlay(session.sessionId),
        customMetadata: { ...emptyCustomMetadata },
        playLineup: { ...emptyApiPlayLineup },
      },
      ballFinals: [],
      isChanged: true,
    } as IApiPlayWithBallFinals;
    return new ReviewDataRow(apiPlay, (session.sessionMetadata.homeTeam?.name ?? ''), (session.sessionMetadata.awayTeam?.name ?? ''));
  };
  const addRowAbove = () => {
    const newRow = createEmptyRow();
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.fromRowIndex);
    rowManager.updateGrid();
  };
  const addRowBelow = () => {
    const newRow = createEmptyRow();
    rowManager.selection && rowManager.insertRowAtPosition(newRow, rowManager.selection.toRowIndex + 1);
    rowManager.updateGrid();
  };
  const deleteRows = () => {
    rowManager.selection &&
      rowManager.deleteRows(rowManager.selection.fromRowIndex, rowManager.selection.toRowIndex - rowManager.selection.fromRowIndex + 1);
    rowManager.updateGrid();
  };

  useEffect(() => {
    if (session.plays) {
      rowManager.parseRows(reviewStore.reviewGame);
      rowManager.setColumns([...BpColumnConfigurations]);
      rowManager.contextMenuOptionsBuilder = new DefaultContextMenuOptionsBuilder(
        {
          addRowAbove,
          addRowBelow,
          deleteRows,
          editValue: () => rowManager.showBulkEditor(),
        },
        []
      );
      rowManager.getMetadataForBulkEditor = (col: IDataGridColumnConfiguration, rows: ReviewDataRow[]) => {
        if (rows.length === 0) {
          return;
        }
        const isHomeTeamBatting = baseballLogicService.isHomeTeamBatting(rows[0].inning.isTopInning);
        switch (col.dataKey) {
          case DataKey.Pitcher:
          case DataKey.Catcher:
            return {
              players: isHomeTeamBatting ? session.awayTeamPlayers : session.homeTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.awayTeam?.name ?? '') : (session.sessionMetadata.homeTeam?.name ?? '')
                ),
            } as IBulkPlayerEditorMetadata;
          case DataKey.Batter:
            return {
              players: isHomeTeamBatting ? session.homeTeamPlayers : session.awayTeamPlayers,
              playerToOutputModelMap: (player: IPlayer | undefined) =>
                playerToReviewPlayerMap(
                  player,
                  isHomeTeamBatting ? (session.sessionMetadata.homeTeam?.name ?? '') : (session.sessionMetadata.awayTeam?.name ?? '')
                ),
            } as IBulkPlayerEditorMetadata;
        }
      };
      rowManager.getSubtitleForHeader = (column: IDataGridColumnConfiguration): React.ReactElement | undefined => {
        switch (column.columnName) {
          case ColumnName.Note: {
            const notes = rowManager.rows.filter((r) => !!r.note.note).length;
            const resolvedNotes = rowManager.rows.filter((r) => !!r.note && r.note.isResolved).length;
            return (
              <div>
                {notes} notes ({resolvedNotes} Resolved)
              </div>
            );
          }
          case ColumnName.Pitcher:
          case ColumnName.Batter: {
            return <div>Team</div>;
          }
        }
        return undefined;
      };
      rowManager.validationService.validators = [noteResolvedValidator];
      rowManager.validateRows();
      rowManager.updateGrid();
    } else {
      rowManager.setRows([]);
    }
  }, [session]);

  const stickyLeftColumnRenderer = (key: string, row: ReviewDataRow, rowIndex: number, style: any): React.ReactElement => {
    return (
      <StatusCell
        key={key}
        style={style}
        cellContent={row.status}
        isWarmup={row.isWarmup}
        convertPitchCallback={() => handleConvertPitch(row, rowIndex)}
        isHidden={row.isHidden()}
      />
    );
  };

  const handleConvertPitch = (row: ReviewDataRow, rowIndex: number) => {
    rowManager.updateRow(rowIndex, DataKey.IsWarmup, !row.isWarmup);
  };
  return (
    <BpPlaysGridWrapper>
      <DataGrid
        rowManager={rowManager}
        stickyColumn={{
          width: STICKY_COLUMN_WIDTH,
          cellRenderer: stickyLeftColumnRenderer,
        }}
      />
    </BpPlaysGridWrapper>
  );
});
